import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { fetchUserActivity } from '../../api/user-api';
import styles from './ClientPanels.module.scss';

const CATEGORY = {
  medicine: 'Lek',
  supplement: 'Suplement',
  water: 'Voda',
  meal: 'Obrok',
  vitamin: 'Vitamin',
  check: 'Provera',
  holy: 'Duhovnost',
  journal: 'Dnevnik',
  sport: 'Sport',
  theraphy: 'Terapija',
  juice: 'Sok',
  tea: 'Čaj',
  tincture: 'Tinktura',
  cream: 'Krema',
  api: 'Api',
};

const pct = (done, scheduled) =>
  scheduled > 0
    ? `${done}/${scheduled} (${Math.round((done / scheduled) * 100)}%)`
    : '-';

const formatDay = (key) => (key ? dayjs(key).format('DD.MM.YYYY.') : '-');

/** Reminders and check-offs over the last 30 days. Needs data:activity:read (API enforced). */
const ClientActivityPanel = ({ userId }) => {
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchUserActivity(userId).then(({ data, success, message }) => {
      if (cancelled) return;
      if (!success) setError(message || 'Aktivnost nije dostupna.');
      else setActivity(data);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const title = (
    <span className={styles.panel__title}>Aktivnost i kalendar (30 dana)</span>
  );

  if (error) {
    return (
      <Card size="small" className={styles.panel} title={title}>
        <Typography.Text type="secondary">{error}</Typography.Text>
      </Card>
    );
  }
  if (!activity) return null;

  const { totals, days, reminders } = activity;

  if (reminders.length === 0) {
    return (
      <Card size="small" className={styles.panel} title={title}>
        <Typography.Text type="secondary">
          Korisnik nema podsetnike u kalendaru.
        </Typography.Text>
      </Card>
    );
  }

  const stat = (label, value) => (
    <div>
      <span className={styles.panel__label}>{label}</span>
      <span className={styles.panel__value}>{value}</span>
    </div>
  );

  const dayClass = (d) => {
    if (d.scheduled === 0)
      return `${styles.panel__day} ${styles['panel__day--none']}`;
    if (d.completed === 0) return styles.panel__day;
    if (d.completed >= d.scheduled)
      return `${styles.panel__day} ${styles['panel__day--full']}`;
    return `${styles.panel__day} ${styles['panel__day--part']}`;
  };

  const columns = [
    {
      title: 'Podsetnik',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <span>
          {name} {!row.isActive && <Tag>neaktivan</Tag>}
        </span>
      ),
    },
    {
      title: 'Kategorija',
      dataIndex: 'category',
      key: 'category',
      render: (c) => CATEGORY[c] || c,
    },
    { title: 'Vreme', dataIndex: 'time', key: 'time', render: (t) => t || '-' },
    {
      title: '7 dana',
      key: 'd7',
      render: (_, r) => pct(r.completed7, r.scheduled7),
    },
    {
      title: '30 dana',
      key: 'd30',
      render: (_, r) => pct(r.completed30, r.scheduled30),
    },
    {
      title: 'Poslednje čekiranje',
      dataIndex: 'lastCompletedDay',
      key: 'last',
      render: formatDay,
    },
  ];

  return (
    <Card size="small" className={styles.panel} title={title}>
      <div className={styles.panel__stats}>
        {stat('Aktivnih podsetnika', totals.activeReminders)}
        {stat('Poslednjih 7 dana', pct(totals.completed7, totals.scheduled7))}
        {stat(
          'Poslednjih 30 dana',
          pct(totals.completed30, totals.scheduled30)
        )}
        {stat(
          'Niz dana zaredom',
          totals.streakAtLeast
            ? `${totals.currentStreak}+`
            : totals.currentStreak
        )}
        {stat('Poslednje čekiranje', formatDay(totals.lastCompletedDay))}
      </div>
      {totals.neverTicked && (
        <Typography.Text type="secondary" className={styles.panel__note}>
          Korisnik ima podsetnike, ali ih nikada ne čekira - procenat ne znači
          da ih ne poštuje.
        </Typography.Text>
      )}
      <div className={styles.panel__strip}>
        {days.map((d) => (
          <Tooltip
            key={d.day}
            title={`${formatDay(d.day)}: ${d.completed}/${d.scheduled}`}
          >
            <div className={dayClass(d)} />
          </Tooltip>
        ))}
      </div>
      <div className={styles.panel__stripLegend}>
        <span>{formatDay(days[0]?.day)}</span>
        <span>danas</span>
      </div>
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={reminders}
        pagination={reminders.length > 10 ? { pageSize: 10 } : false}
      />
    </Card>
  );
};

ClientActivityPanel.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default ClientActivityPanel;
